// src/components/EditProductPage.tsx
import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
    Trash2, Save, Loader2, Image as ImageIcon, 
    Layers, Package, ChevronRight, CheckCircle, AlertCircle, X, ArrowLeft
} from 'lucide-react';

// URL Config
const SPRING_API_URL = '/springboot';
const NODE_API_URL = '/nodejs';

// --- TOAST COMPONENT ---
const Toast = ({ show, message, type, onClose }: any) => {
    if (!show) return null;
    const isSuccess = type === 'success';
    return (
        <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-[110] flex items-center gap-3 px-5 py-3 rounded-lg shadow-xl border animate-in slide-in-from-top-5 fade-in duration-300 ${isSuccess ? 'bg-white border-green-200 text-green-800' : 'bg-white border-red-200 text-red-800'}`}>
            <div className={`p-1.5 rounded-full ${isSuccess ? 'bg-green-100' : 'bg-red-100'}`}>
                {isSuccess ? <CheckCircle size={18} className="text-green-600"/> : <AlertCircle size={18} className="text-red-600"/>}
            </div>
            <div>
                <h4 className="text-sm font-bold">{isSuccess ? 'Success' : 'Error'}</h4>
                <p className="text-xs font-medium opacity-90">{message}</p>
            </div>
            <button onClick={onClose} className="ml-4 p-1 hover:bg-black/5 rounded-full"><X size={14}/></button>
        </div>
    );
};

// Category Type
interface Category {
  id: number;
  name: string;
  subCategories?: Category[];
}

const EditProductPage: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    // --- States ---
    const [productName, setProductName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [minQuantity, setMinQuantity] = useState('');
    const [unit, setUnit] = useState('');
    const [hsnSku, setHsnSku] = useState('');
    const [youtubeUrl, setYoutubeUrl] = useState('');
    
    // Category Logic
    const [nestedCategories, setNestedCategories] = useState<Category[]>([]);
    const [selectedCategoryPath, setSelectedCategoryPath] = useState<number[]>([]);
    
    // Image Handling
    const [existingImages, setExistingImages] = useState<any[]>([]);
    const [newImages, setNewImages] = useState<File[]>([]);
    const [newPreviews, setNewPreviews] = useState<string[]>([]);
    
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    };

    // --- HELPER: Find Path to Category ID ---
    const findCategoryPath = (categories: Category[], targetId: number, currentPath: number[] = []): number[] | null => {
        for (const cat of categories) {
            if (cat.id === targetId) return [...currentPath, cat.id];
            if (cat.subCategories && cat.subCategories.length > 0) {
                const path = findCategoryPath(cat.subCategories, targetId, [...currentPath, cat.id]);
                if (path) return path;
            }
        }
        return null;
    };

    // --- 1. LOAD DATA ---
    useEffect(() => {
        const loadData = async () => {
            const token = localStorage.getItem('token');
            if(!token) {
                showToast("Please login first", "error");
                return;
            }

            try {
                // A. Fetch Categories Tree
                const catRes = await fetch(`${NODE_API_URL}/api/categories`);
                const catTree: Category[] = await catRes.json();
                setNestedCategories(catTree);

                // B. Fetch Product Details
                const prodRes = await fetch(`${SPRING_API_URL}/api/seller/products/${id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (prodRes.ok) {
                    const data = await prodRes.json();
                    
                    setProductName(data.productName);
                    setDescription(data.description);
                    setPrice(data.price);
                    setMinQuantity(data.minimumOrderQuantity);
                    setUnit(data.unit);
                    setHsnSku(data.hsnSku || '');
                    setYoutubeUrl(data.youtubeUrl || '');
                    setExistingImages(data.images || []);

                    // Set Category Path
                    if (data.category && data.category.id) {
                        const foundPath = findCategoryPath(catTree, data.category.id);
                        if (foundPath) setSelectedCategoryPath(foundPath);
                    }
                } else {
                    showToast("Failed to load product details", "error");
                }
            } catch (err) { 
                showToast("Server connection error", "error");
            } finally { 
                setLoading(false); 
            }
        };
        loadData();
    }, [id]);

    // --- 2. HANDLERS ---
    
    const handleCategoryChange = (e: ChangeEvent<HTMLSelectElement>, level: number) => {
        const catId = parseInt(e.target.value);
        const newPath = selectedCategoryPath.slice(0, level);
        if (catId) newPath.push(catId);
        setSelectedCategoryPath(newPath);
    };

    const handleNewFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            setNewImages(prev => [...prev, ...files]);
            const urls = files.map(f => URL.createObjectURL(f));
            setNewPreviews(prev => [...prev, ...urls]);
        }
    };

    const removeNewImage = (index: number) => {
        setNewImages(prev => prev.filter((_, i) => i !== index));
        setNewPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const deleteExistingImage = async (imageId: number) => {
        if(!window.confirm("Permanently delete this image?")) return;
        const token = localStorage.getItem('token');
        try {
             await fetch(`${SPRING_API_URL}/api/seller/products/images/${imageId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
             });
             setExistingImages(existingImages.filter(img => img.id !== imageId));
             showToast("Image removed successfully");
        } catch(e) { showToast("Error removing image", "error"); }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        const token = localStorage.getItem('token');
        
        if (selectedCategoryPath.length === 0) {
            showToast('Please select a category.', "error");
            setSubmitting(false);
            return;
        }

        const finalCategoryId = selectedCategoryPath[selectedCategoryPath.length - 1];
        
        const formData = new FormData();
        formData.append('productName', productName);
        formData.append('description', description);
        formData.append('price', price);
        formData.append('minimumOrderQuantity', minQuantity);
        formData.append('unit', unit);
        formData.append('hsnSku', hsnSku);
        formData.append('categoryId', finalCategoryId.toString());
        formData.append('youtubeUrl', youtubeUrl);

        newImages.forEach(file => formData.append('imageFiles', file));

        try {
            const res = await fetch(`${SPRING_API_URL}/api/seller/products/${id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (res.ok) {
                showToast("Product Updated Successfully!");
                setTimeout(() => navigate('/seller/product'), 1500);
            } else {
                showToast("Update failed", "error");
            }
        } catch (err) { showToast("Error updating product", "error"); } 
        finally { setSubmitting(false); }
    };

    // --- DYNAMIC CATEGORIES ---
    let currentCats = nestedCategories;
    const categoryDropDowns = [];
    
    for (let i = 0; i <= selectedCategoryPath.length; i++) {
        if (!currentCats || currentCats.length === 0) break;
        const val = selectedCategoryPath[i] || '';
        
        categoryDropDowns.push(
            <div key={i} className="mb-4">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    {i === 0 ? 'Main Category' : `Sub Category Level ${i}`}
                </label>
                <div className="relative">
                    <select
                        value={val}
                        onChange={(e) => handleCategoryChange(e, i)}
                        className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#00635e] outline-none bg-white"
                    >
                        <option value="">Select Category</option>
                        {currentCats.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>
            </div>
        );

        if (val) {
            const selectedCat = currentCats.find(c => c.id === val);
            currentCats = selectedCat?.subCategories || [];
        } else {
            break;
        }
    }

    if (loading) return <div className="p-10 text-center text-[#00635e] font-bold animate-pulse">Loading Product Details...</div>;

    return (
        <div className="bg-gray-50/50 min-h-screen font-sans pb-20">
            
            <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />

            {/* HEADER */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
                    <Link to="/seller/product" className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition">
                        <ArrowLeft size={20} />
                    </Link>
                    <div>
                        <h1 className="text-xl font-black text-[#00635e] flex items-center gap-2">
                            Edit Product
                        </h1>
                        <p className="text-xs text-gray-500">Update product details and images.</p>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                    
                    {/* 1. BASIC DETAILS */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="text-sm font-bold text-[#00635e] flex items-center mb-5 border-b pb-2">
                            <Package size={18} className="mr-2 text-red-600"/> Basic Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">PRODUCT NAME *</label>
                                <input className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[#00635e] outline-none" 
                                    value={productName} onChange={e => setProductName(e.target.value)} required />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">PRICE (INR) *</label>
                                <input className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[#00635e] outline-none" 
                                    type="number" value={price} onChange={e => setPrice(e.target.value)} required />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 mb-1">DESCRIPTION *</label>
                                <textarea className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[#00635e] outline-none h-28 resize-none" 
                                    value={description} onChange={e => setDescription(e.target.value)} required />
                            </div>
                        </div>
                    </div>

                    {/* 2. INVENTORY & SPECS */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="text-sm font-bold text-[#00635e] flex items-center mb-5 border-b pb-2">
                            <Layers size={18} className="mr-2 text-red-600"/> Inventory & Specifications
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">MIN ORDER QTY</label>
                                <input className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[#00635e] outline-none" 
                                    value={minQuantity} onChange={e => setMinQuantity(e.target.value)} required />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">UNIT (e.g. Kg, Pcs)</label>
                                <input className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[#00635e] outline-none" 
                                    value={unit} onChange={e => setUnit(e.target.value)} required />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">HSN/SKU CODE</label>
                                <input className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[#00635e] outline-none" 
                                    value={hsnSku} onChange={e => setHsnSku(e.target.value)} />
                            </div>
                        </div>
                    </div>

                    {/* 3. CATEGORY */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="text-sm font-bold text-[#00635e] flex items-center mb-5 border-b pb-2">
                            <ChevronRight size={18} className="mr-2 text-red-600"/> Category Classification
                        </h3>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                            {categoryDropDowns}
                        </div>
                    </div>

                    {/* 4. IMAGES */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="text-sm font-bold text-[#00635e] flex items-center mb-5 border-b pb-2">
                            <ImageIcon size={18} className="mr-2 text-red-600"/> Product Images
                        </h3>
                        
                        {/* Existing Images */}
                        {existingImages.length > 0 && (
                            <div className="mb-6">
                                <p className="text-xs font-bold text-gray-500 mb-3 uppercase">Current Images</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                    {existingImages.map((img: any) => (
                                        <div key={img.id} className="relative group border rounded-lg overflow-hidden h-24 bg-gray-100">
                                            <img src={img.imageUrl} className="w-full h-full object-cover" alt="product" />
                                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button type="button" onClick={() => deleteExistingImage(img.id)} 
                                                    className="bg-red-600 text-white p-1.5 rounded-full shadow hover:bg-red-700 transition-colors">
                                                    <Trash2 size={14}/>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* New Images */}
                        <div>
                            <p className="text-xs font-bold text-gray-500 mb-2 uppercase">Add New Images</p>
                            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition">
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                                    <p className="text-xs text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                                </div>
                                <input type="file" multiple accept="image/*" onChange={handleNewFiles} className="hidden" />
                            </label>

                            {newPreviews.length > 0 && (
                                <div className="mt-4 grid grid-cols-3 md:grid-cols-6 gap-3">
                                    {newPreviews.map((src, i) => (
                                        <div key={i} className="relative h-20 w-20 rounded-lg overflow-hidden border border-blue-200 group">
                                            <img src={src} className="w-full h-full object-cover" alt="preview" />
                                            <button type="button" onClick={() => removeNewImage(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition">
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SUBMIT BUTTON */}
                    <div className="flex justify-end pt-4">
                        <button type="submit" disabled={submitting} className="flex items-center gap-2 bg-red-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-red-700 shadow-lg shadow-red-200 transition active:scale-95 disabled:opacity-70 disabled:scale-100">
                            {submitting ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
                            {submitting ? 'Updating...' : 'Update Product'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditProductPage;